/**
 * Registration page.
 *
 * Phase 17: client-side field validation + friendly API error mapping.
 * Creates a new user + organisation atomically.
 * Design is unchanged from Phase 15 (branded auth card with grouped fields).
 */

import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../lib/api-client';
import { usePageMeta } from '../lib/page-meta';
import AuthAside from '../components/layout/AuthAside';
import Button from '../components/ui/Button';
import {
  capturePartnerReferralCodeFromSearch,
  clearStoredPartnerReferralCode,
  getStoredPartnerReferralCode,
} from '../lib/partner-referrals';

// -------------------------------------------------------------------
// Field-error helpers
// -------------------------------------------------------------------

interface FieldErrors {
  fullName: string | null;
  email: string | null;
  password: string | null;
  organisationName: string | null;
  organisationSlug: string | null;
}

function emptyErrors(): FieldErrors {
  return {
    fullName: null,
    email: null,
    password: null,
    organisationName: null,
    organisationSlug: null,
  };
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

function validate(form: {
  fullName: string;
  email: string;
  password: string;
  organisationName: string;
  organisationSlug: string;
}, t: (key: string) => string): FieldErrors | null {
  const errs = emptyErrors();
  let hasError = false;

  if (!form.fullName.trim()) {
    errs.fullName = t('register.errors.fullNameRequired');
    hasError = true;
  }

  if (!form.email.trim()) {
    errs.email = t('register.errors.emailRequired');
    hasError = true;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errs.email = t('register.errors.emailInvalid');
    hasError = true;
  }

  if (!form.password) {
    errs.password = t('register.errors.passwordRequired');
    hasError = true;
  } else if (form.password.length < 8) {
    errs.password = t('register.errors.passwordLength');
    hasError = true;
  }

  if (!form.organisationName.trim()) {
    errs.organisationName = t('register.errors.organisationNameRequired');
    hasError = true;
  }

  if (!form.organisationSlug.trim()) {
    errs.organisationSlug = t('register.errors.organisationSlugRequired');
    hasError = true;
  } else if (!SLUG_RE.test(form.organisationSlug.trim())) {
    errs.organisationSlug = t('register.errors.organisationSlugInvalid');
    hasError = true;
  }

  return hasError ? errs : null;
}

/**
 * Map an ApiError into a user-facing message with optional field attribution.
 */
function mapApiError(err: ApiError, t: (key: string) => string): {
  form: string | null;
  field?: keyof FieldErrors;
} {
  const msg = err.message ?? '';
  const lower = msg.toLowerCase();
  const code = (err.code ?? '').toLowerCase();

  // Backend validation failures
  if (
    code === 'validation_failed' ||
    code === 'request_validation_failed' ||
    (err.statusCode === 400 && lower.includes('validation'))
  ) {
    if (lower.includes('email')) {
      return { form: t('register.errors.emailConflict'), field: 'email' };
    }
    if (lower.includes('slug') || lower.includes('organisation') || lower.includes('organization')) {
      return {
        form: t('register.errors.slugConflict'),
        field: 'organisationSlug',
      };
    }
    if (lower.includes('password')) {
      return { form: t('register.errors.passwordConflict'), field: 'password' };
    }
    return { form: t('register.errors.genericValidation') };
  }

  // Conflict (duplicate email or slug)
  if (err.statusCode === 409) {
    if (lower.includes('email')) {
      return { form: t('register.errors.emailConflict'), field: 'email' };
    }
    if (lower.includes('slug') || lower.includes('organisation') || lower.includes('organization')) {
      return { form: t('register.errors.slugConflict'), field: 'organisationSlug' };
    }
    return { form: msg || t('register.errors.conflictFallback') };
  }

  // Rate-limited
  if (err.statusCode === 429) {
    return { form: msg };
  }

  // Fallback
  return { form: msg || t('register.errors.unexpected') };
}

export default function RegisterPage() {
  const { t } = useTranslation('auth');
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  usePageMeta({
    title: t('register.metaTitle'),
    description: t('register.metaDescription'),
    canonicalPath: '/register',
    robots: 'noindex,nofollow',
  });

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    organisationName: '',
    organisationSlug: '',
  });

  // Form-level error (banner at top)
  const [formError, setFormError] = useState<string | null>(null);
  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(emptyErrors());

  const update =
    (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear the error for this field on change
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
      setFormError(null);
    };

  // Auto-generate slug from org name (only when slug has not been manually edited)
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    capturePartnerReferralCodeFromSearch(location.search);
  }, [location.search]);
  const handleOrgNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setForm((prev) => {
      const slug = slugTouched
        ? prev.organisationSlug
        : name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
      return { ...prev, organisationName: name, organisationSlug: slug };
    });
    setFieldErrors((prev) => ({ ...prev, organisationName: null }));
    setFormError(null);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, organisationSlug: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, organisationSlug: null }));
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors(emptyErrors());

    // 1. Client-side validation
    const clientErrors = validate(form, t);
    if (clientErrors) {
      setFieldErrors(clientErrors);
      return;
    }

    // 2. API call
    try {
      const result = await register({
        ...form,
        partnerReferralCode: getStoredPartnerReferralCode(),
      }, '/dashboard');
      clearStoredPartnerReferralCode();
      navigate(result.status === 'authenticated' ? '/dashboard' : '/verify-email');
    } catch (err) {
      if (err instanceof ApiError) {
        const { form: formMsg, field } = mapApiError(err, t);
        if (field && formMsg) {
          setFieldErrors((prev) => ({ ...prev, [field]: formMsg }));
        } else if (field) {
          const errorMsg = t('register.errors.genericField');
          setFieldErrors((prev) => ({ ...prev, [field]: errorMsg }));
        } else {
          setFormError(formMsg);
        }
      } else if (err instanceof TypeError) {
        console.error('[RegisterPage] Network error:', err);
        setFormError(t('register.errors.network'));
      } else {
        console.error('[RegisterPage] Unexpected error during registration:', err);
        setFormError(t('register.errors.unexpected'));
      }
    }
  };

  return (
    <div className="acc-auth-page">
      <div className="acc-auth-shell acc-auth-shell--wide">
        <AuthAside />

        <div className="acc-auth-card acc-auth-card--panel">
          <div className="acc-auth-panel__header">
            <div className="acc-auth-panel__eyebrow">{t('register.title')}</div>
            <h1 className="acc-auth-card__title">{t('register.title')}</h1>
            <p className="acc-auth-card__description">
              {t('register.description')}
            </p>
          </div>

          {formError && (
            <div role="alert" className="acc-auth-banner acc-auth-banner--danger">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="acc-auth-form">
            <div className="acc-auth-divider">{t('register.accountSection')}</div>
            <div className="acc-auth-form__grid">
            <div className="acc-auth-form__group">
              <label htmlFor="fullName" className="acc-auth-form__label">{t('register.fullNameLabel')}</label>
              <input
                id="fullName"
                type="text"
                className={`acc-input${fieldErrors.fullName ? ' acc-input--error' : ''}`}
                value={form.fullName}
                onChange={update('fullName')}
                placeholder={t('register.fullNamePlaceholder')}
                autoComplete="name"
                aria-invalid={!!fieldErrors.fullName}
                aria-describedby={fieldErrors.fullName ? 'fullName-error' : undefined}
              />
              {fieldErrors.fullName && (
                <span id="fullName-error" className="acc-auth-form__error" role="alert">
                  {fieldErrors.fullName}
                </span>
              )}
            </div>

            <div className="acc-auth-form__group">
              <label htmlFor="email" className="acc-auth-form__label">{t('register.emailLabel')}</label>
              <input
                id="email"
                type="email"
                className={`acc-input${fieldErrors.email ? ' acc-input--error' : ''}`}
                value={form.email}
                onChange={update('email')}
                autoComplete="email"
                placeholder={t('register.emailPlaceholder')}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
              />
              {fieldErrors.email && (
                <span id="email-error" className="acc-auth-form__error" role="alert">
                  {fieldErrors.email}
                </span>
              )}
            </div>

            <div className="acc-auth-form__group">
              <label htmlFor="password" className="acc-auth-form__label">
                {t('register.passwordLabel')}
                <span className="acc-auth-form__meta">{t('register.passwordHint')}</span>
              </label>
              <input
                id="password"
                type="password"
                className={`acc-input${fieldErrors.password ? ' acc-input--error' : ''}`}
                value={form.password}
                onChange={update('password')}
                autoComplete="new-password"
                placeholder={t('register.passwordPlaceholder')}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              />
              {fieldErrors.password && (
                <span id="password-error" className="acc-auth-form__error" role="alert">
                  {fieldErrors.password}
                </span>
              )}
            </div>

            <div className="acc-auth-form__group">
              <label htmlFor="orgName" className="acc-auth-form__label">{t('register.organisationNameLabel')}</label>
              <input
                id="orgName"
                type="text"
                className={`acc-input${fieldErrors.organisationName ? ' acc-input--error' : ''}`}
                value={form.organisationName}
                onChange={handleOrgNameChange}
                placeholder={t('register.organisationNamePlaceholder')}
                autoComplete="organization"
                aria-invalid={!!fieldErrors.organisationName}
                aria-describedby={fieldErrors.organisationName ? 'orgName-error' : undefined}
              />
              {fieldErrors.organisationName && (
                <span id="orgName-error" className="acc-auth-form__error" role="alert">
                  {fieldErrors.organisationName}
                </span>
              )}
            </div>

            <div className="acc-auth-form__group acc-auth-form__group--span-2">
              <label htmlFor="orgSlug" className="acc-auth-form__label">{t('register.organisationSlugLabel')}</label>
              <input
                id="orgSlug"
                type="text"
                className={`acc-input${fieldErrors.organisationSlug ? ' acc-input--error' : ''}`}
                value={form.organisationSlug}
                onChange={handleSlugChange}
                placeholder={t('register.organisationSlugPlaceholder')}
                aria-invalid={!!fieldErrors.organisationSlug}
                aria-describedby={
                  fieldErrors.organisationSlug ? 'orgSlug-error' : 'orgSlug-hint'
                }
              />
              {fieldErrors.organisationSlug ? (
                <span id="orgSlug-error" className="acc-auth-form__error" role="alert">
                  {fieldErrors.organisationSlug}
                </span>
              ) : (
                <span id="orgSlug-hint" className="acc-auth-form__hint">
                  {t('register.organisationSlugHint')}
                </span>
              )}
            </div>

            <div className="acc-auth-form__actions">
              <Button
                type="submit"
                disabled={isLoading}
                size="lg"
                style={{ width: '100%' }}
              >
                {isLoading ? t('register.submitting') : t('register.submit')}
              </Button>
            </div>
            </div>
          </form>

          <p className="acc-auth-footer acc-auth-footer--panel">
            {t('register.footer')}{' '}
            <Link to="/login" className="acc-link-inline">
              {t('register.footerAction')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
